import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SyncIcon from '@mui/icons-material/Sync';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import SearchIcon from '@mui/icons-material/Search';

import elasticsearchAdminService, { ElasticsearchStatus } from '../../services/elasticsearchAdminService';

const ElasticsearchSettingsPanel: React.FC = () => {
  const [status, setStatus] = useState<ElasticsearchStatus | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(false);

  const [batchSize, setBatchSize] = useState<number>(500);
  const [productId, setProductId] = useState<string>('');

  const [syncLoading, setSyncLoading] = useState(false);
  const [rebuildLoading, setRebuildLoading] = useState(false);
  const [productSyncLoading, setProductSyncLoading] = useState(false);

  const [rebuildDialogOpen, setRebuildDialogOpen] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const fetchStatus = useCallback(async () => {
    setLoadingStatus(true);
    try {
      const data = await elasticsearchAdminService.getStatus();
      setStatus(data);
    } catch (e: any) {
      setToast({ type: 'error', message: e?.message || 'خطا در دریافت وضعیت Elasticsearch' });
      setStatus(null);
    } finally {
      setLoadingStatus(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const statusChip = useMemo(() => {
    if (!status) return <Chip size="small" label="نامشخص" />;
    if (!status.enabled) return <Chip size="small" color="default" label="غیرفعال" />;
    if (status.connected) return <Chip size="small" color="success" label="متصل" />;
    return <Chip size="small" color="warning" label="فعال ولی قطع" />;
  }, [status]);

  const onFullSync = async () => {
    setSyncLoading(true);
    try {
      const result = await elasticsearchAdminService.fullSync(batchSize);
      setToast({
        type: 'success',
        message: `همگام‌سازی انجام شد: ${result.success} موفق، ${result.failed} خطا (کل: ${result.total})`
      });
      await fetchStatus();
    } catch (e: any) {
      setToast({ type: 'error', message: e?.message || 'خطا در همگام‌سازی کامل' });
    } finally {
      setSyncLoading(false);
    }
  };

  const onConfirmRebuild = async () => {
    setRebuildLoading(true);
    try {
      const res = await elasticsearchAdminService.rebuildIndex();
      setToast({ type: 'success', message: res.message || 'ایندکس با موفقیت بازسازی شد' });
      setRebuildDialogOpen(false);
      await fetchStatus();
    } catch (e: any) {
      setToast({ type: 'error', message: e?.message || 'خطا در بازسازی ایندکس' });
    } finally {
      setRebuildLoading(false);
    }
  };

  const onSyncProduct = async () => {
    const id = productId.trim();
    if (!id) return;

    setProductSyncLoading(true);
    try {
      const res = await elasticsearchAdminService.syncProduct(id);
      setToast({ type: 'success', message: res.message || 'محصول با موفقیت همگام‌سازی شد' });
      setProductId('');
      await fetchStatus();
    } catch (e: any) {
      setToast({ type: 'error', message: e?.message || 'خطا در همگام‌سازی محصول' });
    } finally {
      setProductSyncLoading(false);
    }
  };

  return (
    <Box dir="rtl" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {toast && (
        <Alert
          severity={toast.type}
          onClose={() => setToast(null)}
          sx={{ borderRadius: 2 }}
        >
          {toast.message}
        </Alert>
      )}

      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SearchIcon color="primary" />
              <Typography variant="h6" fontWeight={700}>
                تنظیمات Elasticsearch
              </Typography>
              {statusChip}
            </Box>

            <Tooltip title="بروزرسانی وضعیت">
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={loadingStatus ? <CircularProgress size={16} /> : <RefreshIcon />}
                  onClick={fetchStatus}
                  disabled={loadingStatus}
                >
                  بروزرسانی
                </Button>
              </span>
            </Tooltip>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            این بخش برای مدیریت سریع ایندکس‌هاست: مشاهده وضعیت، همگام‌سازی کامل، و بازسازی ایندکس.
          </Typography>

          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography variant="caption" color="text.secondary">
                وضعیت
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {loadingStatus ? '...' : (status ? (status.enabled ? (status.connected ? 'فعال و متصل' : 'فعال ولی قطع') : 'غیرفعال') : 'نامشخص')}
              </Typography>
            </Grid>

            <Grid item xs={12} md={4}>
              <Typography variant="caption" color="text.secondary">
                تعداد Docها (Products index)
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {loadingStatus ? '...' : (status?.stats?.totalDocs ?? '—')}
              </Typography>
            </Grid>

            <Grid item xs={12} md={4}>
              <Typography variant="caption" color="text.secondary">
                اندازه ایندکس
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {loadingStatus ? '...' : (status?.stats?.sizeFormatted ?? '—')}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                همگام‌سازی کامل (Full Sync)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                همه محصولات MongoDB را دوباره به ES ارسال می‌کند (برای به‌روز کردن کامل).
              </Typography>

              <TextField
                fullWidth
                type="number"
                label="Batch Size"
                value={batchSize}
                onChange={(e) => setBatchSize(Math.max(1, Number(e.target.value || 0)))}
                InputProps={{ sx: { fontFamily: 'Vazirmatn' } }}
                helperText="عدد پیشنهادی: 300 تا 1000"
              />
            </CardContent>
            <CardActions sx={{ justifyContent: 'flex-end', p: 2 }}>
              <Button
                variant="contained"
                startIcon={syncLoading ? <CircularProgress size={18} color="inherit" /> : <SyncIcon />}
                onClick={onFullSync}
                disabled={syncLoading}
              >
                شروع Sync
              </Button>
            </CardActions>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ borderRadius: 2, height: '100%', borderColor: 'error.light' }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                بازسازی ایندکس (Rebuild)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                ایندکس محصولات را حذف و دوباره ایجاد می‌کند و سپس Full Sync انجام می‌دهد. (عملیات سنگین)
              </Typography>
              <Alert severity="warning" sx={{ borderRadius: 2 }}>
                پیشنهاد: فقط وقتی نتایج سرچ/ایندکس به‌هم ریخته یا mapping تغییر کرده استفاده کن.
              </Alert>
            </CardContent>
            <CardActions sx={{ justifyContent: 'flex-end', p: 2 }}>
              <Button
                color="error"
                variant="contained"
                startIcon={<DeleteForeverIcon />}
                onClick={() => setRebuildDialogOpen(true)}
                disabled={rebuildLoading}
              >
                Rebuild
              </Button>
            </CardActions>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                همگام‌سازی یک محصول خاص
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                اگر فقط یک محصول مشکل دارد، با Product ID همگام‌سازی‌اش کن.
              </Typography>

              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    label="Product ID"
                    value={productId}
                    onChange={(e) => setProductId(e.target.value)}
                    placeholder="مثلاً 675cd3e4b5e5bd8e5f2b0b1a"
                    InputProps={{ sx: { fontFamily: 'Vazirmatn' } }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={productSyncLoading ? <CircularProgress size={18} /> : <SyncIcon />}
                    onClick={onSyncProduct}
                    disabled={productSyncLoading || !productId.trim()}
                  >
                    Sync این محصول
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={rebuildDialogOpen} onClose={() => setRebuildDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>تایید بازسازی ایندکس</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            این عملیات:
            <br />- ایندکس محصولات را حذف می‌کند
            <br />- دوباره می‌سازد
            <br />- سپس Full Sync انجام می‌دهد
          </Typography>
          <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
            ممکن است چند دقیقه زمان ببرد و در این بازه نتایج جستجو ناپایدار باشد.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRebuildDialogOpen(false)} disabled={rebuildLoading}>
            انصراف
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={onConfirmRebuild}
            startIcon={rebuildLoading ? <CircularProgress size={18} color="inherit" /> : <DeleteForeverIcon />}
            disabled={rebuildLoading}
          >
            تایید و شروع
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ElasticsearchSettingsPanel;


